/** Connectors deliberately left out of the timeline, and why (#946). */
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { formatDuration } from '@/utils/formatDuration';
import type {
  ScheduleExclusionReason,
  ScheduleForecastExcluded,
} from '@/hooks/useScheduleForecast';
import { describeCron } from '@/lib/cron/describe';

const REASON_COPY: Record<ScheduleExclusionReason, string> = {
  no_schedule: 'No schedule configured',
  schedule_paused: 'Schedule paused',
  no_run_in_window: 'No run in the next window',
  invalid_schedule: 'Schedule cannot be read',
};

export interface NotProjectedTableProps {
  excluded: ScheduleForecastExcluded[];
}

export const NotProjectedTable: React.FC<NotProjectedTableProps> = ({ excluded }) => {
  if (!excluded.length) return null;

  return (
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr>
          {['Connector', 'Schedule', 'Median Run Time', 'Reason'].map((h) => (
            <th
              key={h}
              className="border-b px-3 py-2 text-left text-xs font-medium text-muted-foreground"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {excluded.map((row) => (
          <tr key={row.connector_config_id}>
            <td className="border-b px-3 py-2.5">{row.name}</td>
            <td className="border-b px-3 py-2.5">
              {row.cron_expression ? (
                <span title={row.cron_expression}>
                  {describeCron(row.cron_expression, row.timezone)}
                </span>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </td>
            <td className="border-b px-3 py-2.5">
              {row.median_duration_seconds === null ? (
                <Badge variant="secondary">No history</Badge>
              ) : (
                formatDuration(row.median_duration_seconds)
              )}
            </td>
            <td className="border-b px-3 py-2.5">
              <Badge variant="secondary">{REASON_COPY[row.reason] ?? row.reason}</Badge>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
